import { thunkLoadingParams } from 'feature/progressIndicator/progressIndicatorSlice';
import {
	IPaymentDetailsThunkProps,
	IPaymentFeeThunkProps,
	IPaymentRunThunkProps,
	PaymentDetailsType,
} from 'types/Payment';
import { ThunkApiType } from 'types/storeTypes';
import { exportListToExcel } from 'utils/download';
import authFetch, { checkForUnauthorizedResponse } from '../../utils/axios';

export const getPaymentRunThunk = async (
	arg: thunkLoadingParams & IPaymentRunThunkProps,
	thunkAPI: ThunkApiType,
) => {
	let getPaymentRunURL = `/payments/run?paymentRunId=${encodeURIComponent(
		arg.id.split('|')[0],
	)}&paymentRunDate=${arg.id.split('|')[1]}`;

	const {
		profile: { providerId },
	} = thunkAPI.getState().user;

	if (providerId) {
		try {
			const response = await authFetch.get(getPaymentRunURL);
			return response.data;
		} catch (error: ThunkApiType) {
			return checkForUnauthorizedResponse(error, thunkAPI);
		}
	}
};

export const getPaymentDetailsThunk = async (
	arg: thunkLoadingParams & IPaymentDetailsThunkProps,
	thunkAPI: ThunkApiType,
) => {
	let getPaymentDetailsURL = `/payments/run/group?paymentRunId=${encodeURIComponent(
		arg.paymentRunId.split('|')[0],
	)}&paymentRunDate=${arg.paymentRunId.split('|')[1]}&type=${arg.detailType}`;

	const {
		profile: { providerId },
	} = thunkAPI.getState().user;

	if (providerId) {
		try {
			const response = await authFetch.get(getPaymentDetailsURL);
			return { ...response.data, detailType: arg.detailType };
		} catch (error: ThunkApiType) {
			return checkForUnauthorizedResponse(error, thunkAPI);
		}
	}
};

export const getPaymentAssessmentFeeThunk = async (
	arg: thunkLoadingParams & IPaymentFeeThunkProps,
	thunkAPI: ThunkApiType,
) => {
	let getPaymentAssessmentFeeURL = `/payments/run/claim/${arg.claimUUID}`;

	const {
		profile: { providerId },
	} = thunkAPI.getState().user;

	if (providerId) {
		try {
			const response = await authFetch.get(getPaymentAssessmentFeeURL);
			return { ...response.data, detailType: PaymentDetailsType.ASSESSMENT_FEE };
		} catch (error: ThunkApiType) {
			return checkForUnauthorizedResponse(error, thunkAPI);
		}
	}
};

export const downloadPaymentClaimThunk = async (
	{ paymentRunId, claimUUID }: { paymentRunId: string; claimUUID: string },
	thunkAPI: ThunkApiType,
) => {
	const getDownloadPaymentClaim = `/payments/run/claim/${claimUUID}/download`;

	try {
		const response = await authFetch.post(
			getDownloadPaymentClaim,
			{},
			{
				responseType: 'blob',
			},
		);
		exportListToExcel(response.data, `Payment_${paymentRunId}_invoice`, 'PDF');
		return true;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const downloadPaymentGroupThunk = async (
	{
		paymentRunId,
		paymentRunDate,
		type,
	}: {
		paymentRunId: string;
		paymentRunDate: string;
		type: 'EOP' | 'GROUP_INVOICE';
	},
	thunkAPI: ThunkApiType,
) => {
	const getDownloadPaymentGroup = `/payments/run/group/download`;

	const body = {
		paymentRunId,
		paymentRunDate,
		type,
	};

	try {
		const response = await authFetch.post(getDownloadPaymentGroup, body, {
			responseType: 'blob',
		});
		exportListToExcel(response.data, `Payment_${paymentRunId}_${type}`, 'PDF');
		return true;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const downloadPaymentThunk = async (
	{ type }: { type: 'PDF' | 'XLSX' },
	thunkAPI: ThunkApiType,
) => {
	const getDownloadPaymentsListURL = `/payments/download`;

	const { paymentsIds, paymentExcludeIds, downloadAll } =
		thunkAPI.getState().payments;
	const { sortingFields, sortingDirections } = thunkAPI.getState().form;

	let listToInclude = [];
	let listToExclude = [];

	listToInclude = paymentsIds.map((el: string) => ({
		paymentRunDate: el.slice(el.length - 17), //17 is the date length
		paymentRunId: el.slice(0, el.length - 17),
	}));

	listToExclude = paymentExcludeIds.map((el: string) => ({
		paymentRunDate: el.slice(el.length - 17), //17 is the date length
		paymentRunId: el.slice(0, el.length - 17),
	}));

	const values = {
		format: type,
		listToInclude: downloadAll ? [] : listToInclude,
		listToExclude,
		sortingFields,
		sortingDirections,
	};

	try {
		const response = await authFetch.post(getDownloadPaymentsListURL, values, {
			responseType: 'blob',
		});
		exportListToExcel(response.data, 'Payments', type);
		return true;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};
